import { useEffect, useState, useContext } from "react";
import GlobalContext from "../domain/GlobalContext";
import { api } from "../domain/ApiService";
import { Artifact, Assistant } from "../domain/Types";
import {
  IonButton,
  IonIcon,
  IonInput,
  IonModal,
  IonProgressBar,
  IonSpinner,
  IonTextarea,
  ItemReorderEventDetail,
} from "@ionic/react";
import { duplicate, ellipsisHorizontal } from "ionicons/icons";
import ListView from "./ListView";
import Image from "./Image";
import ReactMarkdown from "react-markdown";
import CodeBlock from "./CodeBlock";

type ArtifactsProps = {
  assistant?: Assistant | null;
  handleRedo?: (item: Artifact) => void;
  onUpdate?: (artifacts: Artifact[]) => void;
  preloadedArtifacts?: Artifact[] | undefined;
};

const Artifacts = ({ assistant, handleRedo, onUpdate, preloadedArtifacts }: ArtifactsProps) => {
  const { initiative } = useContext(GlobalContext);
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [isFetched, setIsFetched] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [artifact, setArtifact] = useState<Artifact | null>(null);
  const [contentState, setContentState] = useState<Record<string, any>>({});

  const fetchArtifacts = async () => {

    if (preloadedArtifacts != undefined) {

      if (preloadedArtifacts)
        setArtifacts(preloadedArtifacts);
      else 
        setArtifacts([]);

      return;
    }
    
    if (!assistant) {
      setArtifacts([]);
      setIsFetched(true);
      return;
    }
    setIsFetched(false);
    try {
      console.log("Fetching artifacts for assistant:", assistant.uid);
      const response: Artifact[] = await api(
        "/artifact/list",
        { assistant_uid: assistant.uid },
        initiative
      );
      setArtifacts(response);
      assistant.artifacts = response;
      if (onUpdate) onUpdate(assistant.artifacts);
      window.dispatchEvent(new Event('artifactsUpdated'));
    } catch (err) {
      setArtifacts([]);
      console.error("Error fetching artifacts:", err);
    } finally {
      setIsFetched(true);
    }
  };
  
  const [fadeComplete, setFadeComplete] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    if (isFetched) {
      // Show the element for fade-in
      setFadeComplete(true);
      // Allow the component to render before adding the fade-in class
      // Use requestAnimationFrame for the next repaint
      requestAnimationFrame(() => {
        setIsVisible(true);
      });
    } else {
      // Trigger fade-out animation
      setIsVisible(false);
      // After fade-out duration, remove the element from the DOM
      timer = setTimeout(() => setFadeComplete(false), 1000); // Match the CSS transition duration
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isFetched]);

  
  useEffect(() => {

    if (preloadedArtifacts) {
      // If preloadedArtifacts exists, set it directly
      setArtifacts(preloadedArtifacts);
      setIsFetched(true);
    } else {
      // Otherwise, fetch artifacts as usual
      fetchArtifacts();
    }    
  }, [assistant?.uid, initiative, preloadedArtifacts]);

  const handleEdit = (clickedArtifact: Artifact | null) => {
    if (clickedArtifact) {
      // Editing an existing artifact
      setArtifact(clickedArtifact);
      const editableContent = { ...clickedArtifact.content_json };
      delete editableContent.template_uid;
      delete editableContent.template_assistant_uid;
      setContentState(editableContent);
    } else if (assistant?.artifact_parameters) {

      const artifactParams = typeof assistant.artifact_parameters === "string" 
      ? JSON.parse(assistant.artifact_parameters) 
      : assistant.artifact_parameters;

      // Creating a new artifact based on artifact parameters
      const newContent: Record<string, any> = {};
  
      // Initialize properties from the artifact_parameters schema
      const properties = artifactParams.properties;
      if (properties) {
        Object.keys(properties).forEach((key) => {
          const type = properties[key].type;
          newContent[key] = type === "string" ? "" : null; // Adjust based on expected type
        });
      }
 
      // Set a new empty artifact
      setArtifact({
        initiative_id: initiative?.id,
        initiative_uid: initiative?.uid,
        assistant_uid: assistant?.uid,
        content_json: newContent,
        // Add any other required default values for Artifact type here
      } as Artifact);
  
      setContentState(newContent);
    } else {
      // Reset if no artifact and no artifact parameters are available
      setArtifact(null);
      setContentState({});
    }
  
    setShowModal(!!clickedArtifact || assistant?.artifact_parameters);
  };

  const handleInputChange = (key: string, value: any) => {
    setContentState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const updateArtifact = async () => {
    if (!artifact) return;
    setShowModal(false);
    setIsFetched(false);

    try {
      // Preserve `template_uid` and `template_assistant_uid` when saving
      const updatedArtifact = {
        ...artifact,
        content_json: {
          template_uid: artifact.content_json.template_uid,
          template_assistant_uid: artifact.content_json.template_assistant_uid,
          ...contentState,
        },
      };

      if (artifact.id) {
        await api("/artifact/update", updatedArtifact, initiative);
      } else {
        await api("/artifact/create", updatedArtifact, initiative);
      }
      await fetchArtifacts();
    } catch (err) {
      console.error("Error updating artifact:", err);
    }
  };

  const removeArtifact = async () => {
    if (!artifact) return;
    setShowModal(false);
    setIsFetched(false);

    try {
      await api("/artifact/remove", artifact, initiative);
      await fetchArtifacts();
    } catch (err) {
      console.error("Error removing artifact:", err);
    }
  };

  const redoArtifact = () => {
    setShowModal(false);
    if (handleRedo && artifact) handleRedo(artifact);
  };
  
  const handleReorder = (event: CustomEvent<ItemReorderEventDetail>) => {
    const fromIndex = event.detail.from;
    const toIndex = event.detail.to;
  
    if (fromIndex !== toIndex) {
      // Create a copy of the artifacts array
      const reorderedItems = [...artifacts];
  
      // Move the item within the array
      const [movedItem] = reorderedItems.splice(fromIndex, 1);
      reorderedItems.splice(toIndex, 0, movedItem);
  
      // Update the state immediately to reflect changes in the UI
      setArtifacts(reorderedItems);
  
      // Prepare data for the API call
      const artifactUIDs = reorderedItems.map((artifact) => artifact.uid);

      // alert(fromIndex +" -> "+ toIndex);
      // alert(JSON.stringify(artifactUIDs));
  
      // Make the API call without awaiting it
      api("/artifact/reorder", { artifact_uids: artifactUIDs }, initiative);
  
      // Complete the reorder event by passing the reordered items
      event.detail.complete();
    } else {
      // Complete the reorder event immediately if no change
      event.detail.complete();
    }
  };

  return (
    <>
    <>
    {fadeComplete && (
        <div className={`fade ${isVisible ? 'fade-in' : 'fade-out'}`}>

        {assistant?.artifact_type === 1 && artifacts.length > 0 ? (

          <>
          <IonIcon
            icon={ellipsisHorizontal}
            onClick={handleEdit ? () => handleEdit(artifacts[0]) : undefined}
            className="list-edit-table"
            style={{ marginRight: "17px" }}
            />
          <div style={{ paddingRight: "40px", marginTop: "5px" }}>
          <ReactMarkdown
            className="reactMarkdown"
            components={{
              code({ node, className, children, ...props }: any) {
                const match = /language-(\w+)/.exec(className || '');
                return !props.inline && match ? (
                  <CodeBlock value={String(children).replace(/\n$/, '')} language={match[1]} />
                ) : (
                  <code className={className} {...props}>
                    {children}
                  </code>
                );
              }
            }}
          >
          {artifacts[0].content_json["markdown_content"]}
        </ReactMarkdown>
        </div>
        </>
        ) : (
          <ListView
            items={artifacts}
            artifact_parameters={assistant?.artifact_parameters}
            enableReorder={true}
            defaultLayout="table"
            showHeader={false}
            showActions={true}
            showHeaderActions={true}
            onEdit={handleEdit}
            onReorder={handleReorder}
          />
        )}
        </div>
      )}
      </>

      <IonModal
        className="form-modal"
        onDidDismiss={() => setShowModal(false)}
        isOpen={showModal}
      >
        {artifact ? (
          <div className="form">            
            {Object.keys(contentState).length === 1 ? (
              Object.keys(contentState)[0] === "image_url" ? (
                <Image
                  src={contentState["image_url"]}
                  width="100%"
                  maxWidth="350px"
                  onClick={undefined}
                  isLoading={false}
                  setIsLoading={undefined}
                />
              ) : (
                <IonTextarea
                  className="form-element"
                  label={Object.keys(contentState)[0]}
                  labelPlacement="floating"
                  fill="outline"
                  rows={15}
                  value={contentState[Object.keys(contentState)[0]]}
                  onIonChange={(e) =>
                    handleInputChange(Object.keys(contentState)[0], e.detail.value!)
                  }
                  placeholder="Enter content"
                />
              )
            ) : (
              Object.entries(contentState).map(([key, value]) =>
                key === "image_url" ? (
                  <Image
                    key={key}
                    src={value}
                    width="100%"
                    maxWidth="350px"
                    onClick={undefined}
                    isLoading={false}
                    setIsLoading={undefined}
                  />
                ) : (
                  <IonInput
                    key={key}
                    className="form-element"
                    label={key}
                    labelPlacement="floating"
                    fill="outline"
                    value={typeof value === "string" || typeof value === "number" ? value : ""}
                    onIonChange={(e) => handleInputChange(key, e.detail.value!)}
                    placeholder={`Enter ${key}`}
                  />
                )
              )
            )}
            
            <IonButton onClick={updateArtifact}>Save</IonButton>
            <IonButton className="basic" onClick={redoArtifact}>
              Redo
            </IonButton>
            <IonButton className="basic" onClick={() => setShowModal(false)}>
              Cancel
            </IonButton>
            <IonButton color="danger" onClick={removeArtifact}>
              Remove
            </IonButton>
          </div>
        ) : (
          <div className="form" style={{ minHeight: "160px" }}>
            <div
              className="form-element-title"
              style={{ opacity: "0.5", width: "100%", textAlign: "center" }}
            >
              <IonIcon
                icon={duplicate}
                style={{ width: "60px", height: "60px" }}
                className="menu"
                color={"medium"}
              />
            </div>
            <div
              className="form-element-title"
              style={{ opacity: "0.5", width: "100%", textAlign: "center" }}
            >
              Preparing ...
            </div>
            <div
              className="form-element-title"
              style={{ width: "100%", textAlign: "center" }}
            >
              <IonSpinner
                name="dots"
                color="dark"
                className="large-spinner"
                style={{ margin: "10px" }}
              ></IonSpinner>
            </div>
          </div>
        )}
      </IonModal>
    </>
  );
};

export default Artifacts;